import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/api';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Home, MapPin, TrendingUp } from 'lucide-react';

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authService.login(email, password);
      localStorage.setItem('access_token', response.access_token);
      navigate('/properties');
    } catch (err: unknown) {
      // QA 2026-10-17 (F-002): distinguir la causa real; antes todo era
      // "credenciales inválidas", incluso con el API inalcanzable (CORS/red).
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setError('Credenciales inválidas. Verifica tu email y contraseña.');
      } else if (status === 429) {
        setError('Demasiados intentos. Espera un minuto y vuelve a intentar.');
      } else if (status != null && status >= 500) {
        setError('Error del servidor. Intenta de nuevo en unos minutos.');
      } else if (status == null) {
        setError('No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.');
      } else {
        setError('No se pudo iniciar sesión. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setError('');
    setLoading(true);
    try {
      // Solo login/vinculación: una identidad de Google NUEVA exige registro
      // (invite + consentimientos) y aquí responde 403/422
      const response = await authService.googleLogin({ id_token: idToken });
      localStorage.setItem('access_token', response.access_token);
      navigate('/properties');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 422) {
        setError('Esa cuenta de Google aún no está registrada. Crea tu cuenta en "Regístrate".');
      } else if (status === 503) {
        setError('El inicio de sesión con Google no está disponible por ahora.');
      } else {
        setError('No se pudo iniciar sesión con Google. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-[58%] flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: 'radial-gradient(ellipse at 20% 80%, #263329 0%, #0E1510 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.055]"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="absolute top-0 left-0 w-[3px] h-full bg-primary/50" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <span className="text-white font-semibold text-lg tracking-tight">Takarah</span>
        </div>

        <div className="relative z-10 space-y-5">
          <h2 className="text-white text-[2.6rem] font-semibold leading-[1.15] tracking-tight">
            Tu cartera<br />
            <span className="text-stone-500">inmobiliaria,</span><br />
            siempre en orden.
          </h2>
          <p className="text-stone-500 text-base max-w-sm leading-relaxed">
            Busca, cura y comparte selecciones de propiedades con tus clientes en segundos.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
          {[
            { icon: Home, label: 'Propiedades', value: '150+' },
            { icon: MapPin, label: 'Ciudades', value: '12' },
            { icon: TrendingUp, label: 'Agencias', value: '38' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="space-y-1">
              <Icon className="w-4 h-4 text-stone-600 mb-2" />
              <p className="text-white text-xl font-semibold tracking-tight">{value}</p>
              <p className="text-stone-600 text-xs">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12 bg-background">
        <div className="flex items-center gap-2 mb-12 lg:hidden">
          <Building2 className="w-5 h-5 text-primary" />
          <span className="font-semibold tracking-tight">Takarah</span>
        </div>

        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-semibold tracking-tight mb-1">Bienvenido de vuelta</h1>
          <p className="text-sm text-muted-foreground mb-8">Inicia sesión en tu cuenta</p>

          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="demo@demo.com"
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="error-message">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full transition-all active:scale-[0.98]"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>
          </form>

          <GoogleSignInButton onCredential={handleGoogleCredential} text="signin_with" divider="o" />

          <p className="text-sm text-muted-foreground mt-6 text-center">
            ¿No tienes cuenta?{' '}
            <Link
              to="/register"
              className="text-foreground font-medium hover:text-primary transition-colors"
            >
              Regístrate
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
